import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
} from "react";
import {
  FTLBooking,
  FTLCategoryBufferRule,
  FTLCategoryCountRule,
  FTLEvent,
  FTLEventV2,
  FTLLocation,
  FTLTruck,
  FTLTruckBufferRule,
  FTLTruckCountRule,
  Week,
} from "../types";
import CategoriesContext from "./categories";
import TrucksContext from "./trucks";
import {
  getCategoryBufferRuling as evaluateCategoryBufferRuling,
  getCategoryCountRuling as evaluateCategoryCountRuling,
  getLocationCountRuling as evaluateLocationCountRuling,
  getTruckBufferRuling as evaluateTruckBufferRuling,
  getTruckCountRuling as evaluateTruckCountRuling,
} from "@/lib/ruleEvaluator";

const RulesContext = React.createContext<{
  categoryCountRules: { [key: string]: FTLCategoryCountRule };
  categoryCountRulesLocationOnly: { [key: string]: FTLCategoryCountRule[] };
  categoryBufferRules: { [key: string]: FTLCategoryBufferRule };
  truckCountRules: { [key: string]: FTLTruckCountRule };
  truckBufferRules: { [key: string]: FTLTruckBufferRule };
  getRules: (market?: string) => Promise<void>;
  getCategoryCountRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => { description: string; ok: boolean };
  getCategoryBufferRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => { description: string; ok: boolean };
  getTruckCountRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => { description: string; ok: boolean };
  getTruckBufferRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => { description: string; ok: boolean };
  getLocationCountRuling: (
    booking?: FTLBooking,
    event?: FTLEventV2
  ) => { description: string; ok: boolean };
}>({
  categoryCountRules: {},
  categoryCountRulesLocationOnly: {},
  categoryBufferRules: {},
  truckCountRules: {},
  truckBufferRules: {},
  getRules: (market?: string) => {
    return Promise.resolve();
  },
  getCategoryCountRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => ({ description: "", ok: true }),
  getCategoryBufferRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => ({ description: "", ok: true }),
  getTruckCountRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => ({ description: "", ok: true }),
  getTruckBufferRuling: (
    booking?: FTLBooking,
    truck?: FTLTruck,
    bookings?: FTLBooking[]
  ) => ({ description: "", ok: true }),
  getLocationCountRuling: (booking?: FTLBooking, location?: FTLLocation) => ({
    description: "",
    ok: true,
  }),
});

export function RulesProvider(props: { children: React.ReactNode }) {
  const trucksContext = useContext(TrucksContext);
  const categoriesContext = useContext(CategoriesContext);

  const [categoryCountRules, setCategoryCountRules] = useState<{
    [key: string]: FTLCategoryCountRule;
  }>({});
  const [categoryCountRulesLocationOnly, setCategoryCountRulesLocationOnly] =
    useState<{ [key: string]: FTLCategoryCountRule[] }>({});
  const [categoryBufferRules, setCategoryBufferRules] = useState<{
    [key: string]: FTLCategoryBufferRule;
  }>({});
  const [truckCountRules, setTruckCountRules] = useState<{
    [key: string]: FTLTruckCountRule;
  }>({});
  const [truckBufferRules, setTruckBufferRules] = useState<{
    [key: string]: FTLTruckBufferRule;
  }>({});

  const evaluatorState = useMemo(
    () => ({
      categoryCountRules,
      categoryCountRulesLocationOnly,
      categoryBufferRules,
      truckCountRules,
      truckBufferRules,
      trucks: trucksContext.trucks,
      categories: categoriesContext.categories,
    }),
    [
      categoryCountRules,
      categoryCountRulesLocationOnly,
      categoryBufferRules,
      truckCountRules,
      truckBufferRules,
      trucksContext.trucks,
      categoriesContext.categories,
    ]
  );

  const getRules = (market?: string) => {
    return new Promise<void>(async (resolve, reject) => {
      const response = await fetch(`/api/rules?market=${market}`);
      const data = await response.json();
      if (data?.categoryCountRules) {
        const categoryCountRules = data.categoryCountRules.reduce(
          (
            obj: { [key: string]: FTLCategoryCountRule },
            rule: FTLCategoryCountRule
          ) => {
            rule.events.forEach((event: string) => {
              rule.categories.forEach((category: string) => {
                obj[`${event}-${category}`] = rule;
              });
            });
            return obj;
          },
          {}
        );
        setCategoryCountRules(categoryCountRules);

        const categoryCountRulesForLocation = data.categoryCountRules.reduce(
          (
            obj: { [key: string]: FTLCategoryCountRule[] },
            rule: FTLCategoryCountRule
          ) => {
            rule.events.forEach((event) => {
              if (!obj[`${event}`]) {
                obj[`${event}`] = [rule];
              } else {
                obj[`${event}`].push(rule);
              }
            });
            return obj;
          },
          {}
        );
        setCategoryCountRulesLocationOnly(categoryCountRulesForLocation);
      }

      if (data?.categoryBufferRules) {
        const categoryBufferRules = data.categoryBufferRules.reduce(
          (
            obj: { [key: string]: FTLCategoryBufferRule },
            rule: FTLCategoryBufferRule
          ) => {
            rule.events.forEach((event) => {
              rule.categories.forEach((category) => {
                obj[`${event}-${category}`] = rule;
              });
            });
            return obj;
          },
          {}
        );
        setCategoryBufferRules(categoryBufferRules);
      }

      if (data?.truckCountRules) {
        const truckCountRules = data.truckCountRules.reduce(
          (
            obj: { [key: string]: FTLTruckCountRule },
            rule: FTLTruckCountRule
          ) => {
            obj[`${rule.event}-${rule.truck}`] = rule;
            return obj;
          },
          {}
        );
        setTruckCountRules(truckCountRules);
      }

      if (data?.truckBufferRules) {
        const truckBufferRules = data.truckBufferRules.reduce(
          (
            obj: { [key: string]: FTLTruckBufferRule },
            rule: FTLTruckBufferRule
          ) => {
            obj[`${rule.event}-${rule.truck}`] = rule;
            return obj;
          },
          {}
        );
        setTruckBufferRules(truckBufferRules);
      }

      resolve();
    });
  };

  const getCategoryCountRuling = useCallback(
    (booking?: FTLBooking, truck?: FTLTruck, bookings?: FTLBooking[]) => {
      return evaluateCategoryCountRuling(evaluatorState, { booking, truck, bookings });
    },
    [evaluatorState]
  );

  const getCategoryBufferRuling = useCallback(
    (booking?: FTLBooking, truck?: FTLTruck, bookings?: FTLBooking[]) => {
      return evaluateCategoryBufferRuling(evaluatorState, {
        booking,
        truck,
        bookings,
      });
    },
    [evaluatorState]
  );

  const getTruckCountRuling = useCallback(
    (booking?: FTLBooking, truck?: FTLTruck, bookings?: FTLBooking[]) => {
      return evaluateTruckCountRuling(evaluatorState, { booking, truck, bookings });
    },
    [evaluatorState]
  );

  const getTruckBufferRuling = useCallback(
    (booking?: FTLBooking, truck?: FTLTruck, bookings?: FTLBooking[]) => {
      return evaluateTruckBufferRuling(evaluatorState, {
        booking,
        truck,
        bookings,
      });
    },
    [evaluatorState]
  );

  const getLocationCountRuling = useCallback(
    (booking?: FTLBooking, event?: FTLEventV2) => {
      return evaluateLocationCountRuling(booking, event);
    },
    []
  );

  return (
    <RulesContext.Provider
      value={{
        getRules,
        categoryCountRules,
        categoryCountRulesLocationOnly,
        categoryBufferRules,
        truckCountRules,
        truckBufferRules,
        getCategoryCountRuling,
        getCategoryBufferRuling,
        getTruckCountRuling,
        getTruckBufferRuling,
        getLocationCountRuling,
      }}
    >
      {props.children}
    </RulesContext.Provider>
  );
}

export default RulesContext;
